import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useReactFlow,
  type Connection,
  type Edge,
  type IsValidConnection,
  type NodeChange,
  type NodeDimensionChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './flows.css';
import { useMemo, useState, type DragEvent, type RefObject } from 'react';
import { createBlock, isBlockType, parsePortHandle } from '../../domain/blocks';
import {
  applyConnection,
  applyPositions,
  TARGET_HANDLE,
  toCanvasEdges,
  toCanvasNodes,
} from '../../domain/canvas';
import { addGroup } from '../../domain/document';
import { takenIds } from '../../domain/ids';
import { CardNode, type CardFlowNode } from './CardNode';
import { CARD_DRAG, DRAG_MIME, selectedEdgeId, selectedGroupId, useBuilder } from './context';

const nodeTypes = { card: CardNode };
type XY = { x: number; y: number };
type Size = NonNullable<NodeDimensionChange['dimensions']>;

const isValidConnection: IsValidConnection = (c) =>
  c.targetHandle === TARGET_HANDLE && parsePortHandle(c.sourceHandle) !== null;

/**
 * O canvas: cards arrastáveis, ligações das saídas ao início dos cards,
 * zoom, minimapa e controles. Soltar um item da paleta no vazio cria um card.
 *
 * Os nós nascem do documento; o canvas só guarda o que é dele — o tamanho
 * medido de cada card e a posição durante um arrasto (vai para o documento
 * quando o arrasto termina, para não salvar a cada pixel).
 */
export function Canvas({ wrapper }: { wrapper: RefObject<HTMLDivElement | null> }) {
  const { doc, issues, selection, select, update } = useBuilder();
  const flow = useReactFlow();
  const [moving, setMoving] = useState<ReadonlyMap<string, XY>>(new Map());
  const [sizes, setSizes] = useState<Readonly<Record<string, Size>>>({});
  const groupSel = selectedGroupId(selection);
  const edgeSel = selectedEdgeId(selection);

  const nodes = useMemo<CardFlowNode[]>(
    () =>
      toCanvasNodes(doc).map((n) => ({
        ...n,
        position: moving.get(n.id) ?? n.position,
        measured: sizes[n.id],
        selected: n.id === groupSel,
      })),
    [doc, moving, sizes, groupSel],
  );

  const edges = useMemo<Edge[]>(
    () =>
      toCanvasEdges(doc).map((e) => {
        const level = issues.edges.get(e.id);
        return {
          ...e,
          selected: e.id === edgeSel,
          className: level ? `flow-edge-${level}` : undefined,
          markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
        };
      }),
    [doc, issues, edgeSel],
  );

  const onNodesChange = (changes: NodeChange<CardFlowNode>[]) => {
    const measured: Record<string, Size> = {};
    const positions = new Map<string, XY>();
    let finished = false;
    for (const c of changes) {
      if (c.type === 'dimensions' && c.dimensions) measured[c.id] = c.dimensions;
      if (c.type === 'position') {
        if (c.position) positions.set(c.id, c.position);
        if (!c.dragging) finished = true;
      }
    }
    if (Object.keys(measured).length) setSizes((s) => ({ ...s, ...measured }));
    if (finished) {
      const all = new Map([...moving, ...positions]);
      update((d) => applyPositions(d, all));
      setMoving(new Map());
    } else if (positions.size) {
      setMoving((m) => new Map([...m, ...positions]));
    }
  };

  const onDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (e: DragEvent) => {
    const kind = e.dataTransfer.getData(DRAG_MIME);
    if (!kind) return;
    e.preventDefault();
    // O ponto do mouse vira o canto de cima do card, um pouco à esquerda.
    const at = flow.screenToFlowPosition({ x: e.clientX - 40, y: e.clientY - 20 });
    const block = isBlockType(kind) ? createBlock(kind, takenIds(doc)) : null;
    if (!block && kind !== CARD_DRAG) return;
    const r = addGroup(doc, at, block ? [block] : []);
    update(() => r.doc);
    select(block ? { groupId: r.groupId, blockId: block.id } : { groupId: r.groupId });
  };

  return (
    <div ref={wrapper} className="flow-canvas h-full w-full" onDragOver={onDragOver} onDrop={onDrop}>
      <ReactFlow<CardFlowNode, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onConnect={(c: Connection) => update((d) => applyConnection(d, c))}
        isValidConnection={isValidConnection}
        onNodeClick={(_, node) => select({ groupId: node.id })}
        onEdgeClick={(_, edge) => select({ edgeId: edge.id })}
        onPaneClick={() => select(null)}
        deleteKeyCode={null}
        selectionKeyCode={null}
        multiSelectionKeyCode={null}
        connectionRadius={28}
        snapToGrid
        snapGrid={[8, 8]}
        minZoom={0.2}
        maxZoom={1.75}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        colorMode="light"
        attributionPosition="bottom-left"
        aria-label="Canvas do fluxo"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1.5} />
        <Controls position="bottom-left" showInteractive={false} style={{ marginBottom: 32 }} />
        <MiniMap position="bottom-right" pannable zoomable ariaLabel="Minimapa do fluxo" />
      </ReactFlow>
    </div>
  );
}
